import newApiPublic from "../../../services/newApi/api_public";
import { unwrapField } from "../../../services/newApi/unwrap";
import authLogger from "../services/authLogger";

const preview = (token: string | null | undefined): string | null =>
  token ? `${token.slice(0, 8)}…${token.slice(-4)}` : null;

class CsrfService {
  private csrfToken: string | null = null;

  /**
   * Busca o token CSRF do servidor.
   * O servidor setará o cookie httpOnly e retornará o token no corpo da resposta.
   */
  async fetchToken(): Promise<string | null> {
    authLogger.log("csrf", "GET /auth/csrf");
    try {
      const response = await newApiPublic.get("/auth/csrf");
      const token = unwrapField<string>(response.data, "csrfToken") ?? null;
      const hasEnvelope =
        !!response.data &&
        typeof response.data === "object" &&
        "data" in (response.data as Record<string, unknown>);
      authLogger.log("csrf", "← /auth/csrf response", {
        status: response.status,
        hasEnvelope,
        hasTokenInBody: !!token,
      });
      this.csrfToken = token;
      if (!token) {
        authLogger.warn(
          "csrf",
          "/auth/csrf retornou sem csrfToken legível",
          response.data,
        );
      }
      return this.csrfToken;
    } catch (error: any) {
      authLogger.error("csrf", "fetchToken falhou", {
        status: error?.response?.status,
        message: error?.message,
      });
      throw new Error(error.response?.data?.message || "Falha ao obter token CSRF");
    }
  }

  /**
   * Retorna o token CSRF armazenado em memória.
   */
  getToken(): string | null {
    return this.csrfToken;
  }

  clearToken(): void {
    authLogger.log("csrf", "token limpo");
    this.csrfToken = null;
  }

  setToken(token: string): void {
    this.csrfToken = token;
    authLogger.log("csrf", "token atualizado via setToken", {
      tokenPreview: preview(token),
    });
  }

  /**
   * Popula o csrfToken a partir da resposta de login/MFA/refresh, tolerando
   * ambos os shapes (envelope BaseController.ok ou jsonResponse direto).
   * No-op silencioso se a resposta não trouxer o campo.
   */
  setFromResponse(responseData: unknown): void {
    const token = unwrapField<string>(responseData, "csrfToken");
    if (token) {
      this.csrfToken = token;
      authLogger.log("csrf", "token atualizado via setFromResponse", {
        tokenPreview: preview(token),
      });
    }
  }

  /**
   * Retorna o objeto de header pronto para ser usado no Axios.
   */
  getHeaders(): Record<string, string> {
    return this.csrfToken ? { "x-csrf-token": this.csrfToken } : {};
  }
}

export default new CsrfService();
